import React, { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, PhoneCall } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useRequests } from "@/contexts/RequestsContext";
import { toast } from "sonner";

export default function ServiceOrder() {
  const { add } = useRequests();
  const [params] = useSearchParams();
  const slug = params.get("service") || "";
  const serviceName = params.get("service_name") || "Service TeamWork";
  const packageName = params.get("package") || "";
  const price = params.get("package_price") || "";
  const duration = params.get("package_duration") || "";
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", city: "Kinshasa", need: "" });
  const valid = useMemo(() => form.name.trim() && form.phone.trim() && form.need.trim(), [form]);

  if (!slug || !packageName || !price) return <Navigate to="/univers/services" replace />;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      const rec = await add({
        sector: "services",
        transaction: "request",
        project: `Service · ${serviceName} · ${packageName}`,
        name: form.name.trim(),
        phone: form.phone.trim(),
        whatsapp: form.phone.trim(),
        email: form.email.trim(),
        service_id: slug,
        country: "CD",
        city: form.city.trim() || "Kinshasa",
        zone: "",
        budget_min: price,
        budget_max: price,
        criteria: form.need.trim(),
        details: {
          type_service: slug,
          service_name: serviceName,
          formule: packageName,
          prix_affiche_usd: Number(price),
          delai_annonce: duration,
          entreprise: form.company.trim(),
          pricing_status: "prix_indicatif_a_confirmer",
          next_step: "appel_qualification_puis_paiement",
        },
      });
      setConfirmed(rec);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible d'envoyer la demande. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) return (
    <div className="container-app max-w-xl py-12">
      <div className="rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Check className="h-8 w-8" /></div>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-brand-navy">Demande reçue</h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">TeamWork vous contacte pour confirmer le périmètre, le prix final et le mode de paiement avant le démarrage.</p>
        <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-left text-sm">
          <div className="font-bold text-brand-navy">{serviceName}</div>
          <div className="mt-1 text-slate-500">{packageName} · {price} USD · {duration}</div>
          <div className="mt-2 text-xs text-slate-400">Référence : {confirmed.number}</div>
        </div>
        <Link to="/demandes" className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-xl bg-brand-navy px-4 text-sm font-extrabold text-white">Suivre ma demande</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] pb-16">
      <div className="container-app max-w-2xl py-5 sm:py-10">
        <Link to={`/services/${slug}`} className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy"><ArrowLeft className="h-4 w-4" /> Retour à l’offre</Link>
        <div className="mt-4 rounded-3xl bg-brand-navy p-6 text-white shadow-lg">
          <p className="text-[10px] font-black uppercase tracking-[.2em] text-brand-orange">Votre choix</p>
          <h1 className="mt-2 font-display text-2xl font-extrabold text-white">{serviceName}</h1>
          <div className="mt-4 flex flex-wrap gap-2 text-sm font-bold"><span className="rounded-full bg-white/10 px-3 py-2">{packageName}</span><span className="rounded-full bg-white/10 px-3 py-2">{price} USD</span><span className="rounded-full bg-white/10 px-3 py-2">{duration}</span></div>
        </div>

        <form onSubmit={submit} className="mt-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <div className="flex items-start gap-3 rounded-2xl bg-orange-50 p-4"><PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" /><div><div className="font-bold text-brand-navy">Pas de paiement maintenant</div><p className="mt-1 text-xs leading-5 text-slate-600">Envoyez vos coordonnées. Nous vous appelons, confirmons le besoin et le prix, puis vous recevez le lien de paiement.</p></div></div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div><Label>Nom complet *</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Votre nom" /></div>
            <div><Label>Téléphone / WhatsApp *</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} placeholder="+243…" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="vous@entreprise.com" /></div>
            <div><Label>Entreprise</Label><Input value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})} placeholder="Nom de votre entreprise" /></div>
            <div className="sm:col-span-2"><Label>Ville</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} /></div>
            <div className="sm:col-span-2"><Label>Décrivez votre besoin *</Label><Textarea rows={5} value={form.need} onChange={(e)=>setForm({...form,need:e.target.value})} placeholder="Ce que vous voulez lancer, améliorer ou digitaliser…" /></div>
          </div>
          <Button type="submit" disabled={!valid || submitting} className="mt-6 h-12 w-full bg-brand-orange font-extrabold hover:bg-brand-orange/90">{submitting ? "Envoi…" : `Envoyer ma demande · ${price} USD`}</Button>
          <p className="mt-3 text-center text-[11px] leading-4 text-slate-400">Le prix affiché est la base de la formule choisie. Toute demande hors périmètre est confirmée avec vous avant paiement.</p>
        </form>
      </div>
    </div>
  );
}
